import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Client-safe feature flag hook.
 * Reads from the public-readable `feature_flags` table (RLS allows SELECT for all).
 * Returns `true` (enabled) by default if the flag is not found (soft-allow).
 */
export function useFeatureFlag(flagKey: string) {
  const { data: enabled = true, isLoading } = useQuery({
    queryKey: ['feature-flag', flagKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('is_enabled')
        .eq('flag_key', flagKey)
        .maybeSingle();

      // Soft-allow if not found or error
      if (error || !data) return true;
      return data.is_enabled;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return { enabled, isLoading };
}

/**
 * Admin-side hook using adminSupabase.
 * Useful for the Control Tower UI to manage flags.
 */
export function useFeatureFlagAdmin(flagKey: string) {
  const { data: enabled = true, isLoading } = useQuery({
    queryKey: ['feature-flag-admin', flagKey],
    queryFn: async () => {
      const mod = await import('@/integrations/supabase/adminClient');
      const client = mod.adminSupabase;
      const { data, error } = await client
        .from('feature_flags')
        .select('is_enabled')
        .eq('flag_key', flagKey)
        .maybeSingle();

      if (error || !data) return true;
      return data.is_enabled;
    },
    staleTime: 5 * 60 * 1000,
  });

  return { enabled, isLoading };
}
